import { useMemo, useRef, useEffect, useState } from "react";
import "./fleetViewDetails.css";
import { computeAxleLoadPct } from "../../utils/fleetUtils";
import defaultTruckImg from "../../assets/logo_vehicle.png";
import FleetMessagePrompt from "../../component/fleet-message/FleetMessagePrompt";

const AXLE_KEYS = ["steer", "drive", "trailerA", "trailerB"];
const AXLE_LABELS = {
  steer: "STEER",
  drive: "DRIVE",
  trailerA: "TRAILER A",
  trailerB: "TRAILER B",
};

const formatWeight = (val) => {
  if (!val) return "--";
  return `${(val / 1000).toFixed(1)}t`;
};

const FleetViewDetails = ({ selectedFleet, onClose }) => {
  const scrollWrapperRef = useRef(null);

  const [showMessagePrompt, setShowMessagePrompt] = useState(false);

  const axleLoadPct = useMemo(() => {
    return computeAxleLoadPct(
      selectedFleet?.weights,
      selectedFleet?.weightsMax,
    );
  }, [selectedFleet?.weights, selectedFleet?.weightsMax]);

  // Global Listeners
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Mobile Swipe-to-Close Logic
  useEffect(() => {
    const element = scrollWrapperRef.current;
    if (!element) return;

    let touchStartY = 0;
    let touchEndY = 0;
    let scrollBottomAtStart = false;

    const handleTouchStart = (e) => {
      if (
        e.target.closest("button") ||
        e.target.closest("a") ||
        e.target.closest("input") ||
        e.target.closest("textarea")
      ) {
        return;
      }

      touchStartY = e.touches[0].clientY;

      // Check if user is at the bottom of the scroll area
      // scrollBottomAtStart = element.scrollHeight - element.scrollTop;
      scrollBottomAtStart =
        element.scrollHeight - element.scrollTop - element.clientHeight < 5;
    };

    const handleTouchMove = (e) => {
      const currentY = e.touches[0].clientY;
      const delta = touchStartY - currentY; // positive when swiping up

      // Only trigger if already at bottom and swiping up
      if (scrollBottomAtStart && delta > 50 && delta < 200) {
        element.style.transform = `translateY(-${delta}px)`;
        element.style.opacity = `${1 - delta / 200}`;
        e.preventDefault();
      }
      touchEndY = currentY;
    };

    const handleTouchEnd = () => {
      const delta = touchStartY - touchEndY;

      if (scrollBottomAtStart && delta > 150) {
        onClose();
      } else {
        element.style.transform = "";
        element.style.opacity = "";
      }
      touchStartY = 0;
      touchEndY = 0;
    };

    element.addEventListener("touchstart", handleTouchStart);
    element.addEventListener("touchmove", handleTouchMove);
    element.addEventListener("touchend", handleTouchEnd);
    return () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
    };
  }, [selectedFleet, onClose]);

  if (!selectedFleet) return null;

  const status = selectedFleet?.status;
  const statusIcon =
    status === "online"
      ? "bi-bolt-fill"
      : status === "idle"
        ? "bi-pause-fill"
        : "bi-moon-fill";
  const hasCriticalLoad = Object.values(axleLoadPct).some((pct) => pct > 90);

  const imgSource = selectedFleet?.imageUrl
    ? selectedFleet.imageUrl
    : defaultTruckImg;

  if (import.meta.env.DEV) {
    console.log("debug: ujj img src: ", imgSource);
  }

  const ts = selectedFleet?.serverTimestamp;
  const lastSeen = ts
    ? (() => {
        const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
        // if (diff < 60) return `${diff}s ago`;
        if (diff < 60) return null; //don't show secs update. irrelevant
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;

        return `${Math.floor(diff / 86400)}d ago`;
      })()
    : null;

  return (
    <div
      ref={scrollWrapperRef}
      className={`fleet-details-container ${hasCriticalLoad ? "critical-alert" : ""}`}
    >
      <div className="fd-card">
        {/* HEADER SECTION - Combined Basic Info */}
        <div className="fd-header">
          <div className="fd-header-img">
            <img className="fd-v-img" src={imgSource} alt="Vehicle" />

            <div className={`fd-status-badge ${status}`}>
              <i className={`bi ${statusIcon}`} />
            </div>
          </div>

          <div className="fd-header-title">
            {/* rego */}
            <h3>{selectedFleet?.vehicleCode || "Unknown"}</h3>
            {/* updated */}
            {lastSeen ? (
              <span className="fd-updated-ts">
                <i className="bi bi-clock" /> Updated {lastSeen}
              </span>
            ) : null}

            {/* <div className="fd-header-address">
              <i className="bi bi-geo-alt-fill fd-address-icon" />
              <span className="fd-address-text">
                {selectedFleet?.address?.split(",")[0]}
              </span>
            </div> */}
          </div>

          <button className="fd-close" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* AXLE DISTRIBUTION - Two columns on desktop */}
        <div className="fd-weight-container">
          <div className="fd-weight-header">
            <span className="fd-weight-title">AXLE DISTRIBUTION</span>
            <span className="fd-weight-badge">
              {formatWeight(selectedFleet?.totalWeight)}
            </span>
          </div>

          <div className="fd-weight-axles">
            {AXLE_KEYS.map((key) => {
              const current = selectedFleet?.weights?.[key] || 0;
              const pct = axleLoadPct[key] || 0;
              return (
                <div className="fd-axle-card" key={key}>
                  <div className="fd-axle-card-header">
                    <span className="fd-axle-name">{AXLE_LABELS[key]}</span>
                    <span className="fd-axle-weight">
                      {current.toLocaleString()}kg
                    </span>
                  </div>
                  <div className="fd-axle-bar">
                    <div
                      className={`fd-axle-bar-fill ${pct > 90 ? "critical" : ""}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* address */}
        <div className="fd-address-container">
          <i className="bi bi-geo-alt-fill fd-address-icon" />
          <div className="fd-address-text">
            {selectedFleet?.address || "Detecting coordinates..."}
          </div>
        </div>

        {/* TELEMETRY GRID - 4 columns to save vertical space */}
        <div className="fd-stats-grid">
          <div className="fd-stat">
            <span className="fd-label">Fuel</span>
            <strong>
              {selectedFleet?.fuel?.value || "--"} {selectedFleet?.fuel?.unit}
            </strong>
          </div>
          <div className="fd-stat">
            <span className="fd-label">Volt</span>
            <strong>{selectedFleet?.voltage?.value || "--"}V</strong>
          </div>
          <div className="fd-stat">
            <span className="fd-label">Speed</span>
            <strong>{selectedFleet?.speedKmh ?? 0}</strong>
          </div>
          <div className="fd-stat">
            <span className="fd-label">Sats</span>
            <strong>{selectedFleet?.sourceMeta?.sats || 0}</strong>
          </div>
        </div>

        {/* DRIVER SECTION - footer style */}
        {selectedFleet?.employee && (
          <div className="fd-driver-details-section">
            <div className="fd-driver-avatar">
              {selectedFleet.employee.name?.charAt(0) || "👤"}
            </div>

            <div className="fd-driver-info">
              <span className="fd-d-id">
                ID: {selectedFleet.employee.employeeCode}
              </span>
              <span className="fd-d-name">
                {/* {selectedFleet.employee.name?.split(" ")[0]} */}
                {selectedFleet.employee.name}
              </span>
            </div>

            <div className="fd-driver-actions">
              {/* <button
                className="fd-btn fd-btn-primary"
                onClick={() =>
                  (window.location.href = `tel:${selectedFleet.employee.phone || ""}`)
                }
              >
                <i className="bi bi-telephone-fill" />
                <span>Call</span>
              </button> */}
              <button
                className="fd-btn fd-btn-ghost"
                onClick={(e) => {
                  console.log("MESSAGE CLICKED");
                  setShowMessagePrompt(true);
                  e.preventDefault();
                }}
              >
                <i className="bi bi-chat-left-dots-fill" />
                <span>MESSAGE</span>
              </button>
            </div>
          </div>
        )}

        {/* fleet message prompt */}
        {showMessagePrompt && (
          <FleetMessagePrompt
            fleet={selectedFleet}
            onClose={() => setShowMessagePrompt(false)}
            onSent={() => setShowMessagePrompt(false)}
          />
        )}
      </div>
    </div>
  );
};

export default FleetViewDetails;
